import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const MOCK_REVENUE = [
  { month: 'Oct', revenue: 12, downloads: 34 },
  { month: 'Nov', revenue: 28, downloads: 67 },
  { month: 'Dec', revenue: 45, downloads: 112 },
  { month: 'Jan', revenue: 38, downloads: 89 },
  { month: 'Feb', revenue: 62, downloads: 145 },
  { month: 'Mar', revenue: 87, downloads: 198 },
];

export default function CreatorAnalytics({ games }) {
  const [isOpen, setIsOpen] = useState(false);

  const totalViews = games?.reduce((acc, g) => acc + (g?.views || 0), 0);
  const totalDownloads = games?.reduce((acc, g) => acc + Math.floor((g?.views || 0) * 0.12), 0);
  const totalRemixes = games?.reduce((acc, g) => acc + Math.floor((g?.views || 0) * 0.04), 0);
  const totalRevenue = games?.filter((g) => g?.published)?.reduce((acc, g) => acc + Math.floor((g?.views || 0) * 0.008), 0);
  const totalCreations = games?.length;

  const maxRevenue = Math.max(...MOCK_REVENUE?.map((d) => d?.revenue));
  const maxDownloads = Math.max(...MOCK_REVENUE?.map((d) => d?.downloads));

  const ANALYTICS_STATS = [
    { label: 'Total Creations', value: totalCreations, icon: 'Layers', color: 'var(--color-primary)' },
    { label: 'Total Views', value: totalViews?.toLocaleString(), icon: 'Eye', color: 'var(--color-secondary)' },
    { label: 'Downloads', value: totalDownloads?.toLocaleString(), icon: 'Download', color: '#10B981' },
    { label: 'Remixes', value: totalRemixes?.toLocaleString(), icon: 'Shuffle', color: 'var(--color-accent)' },
    { label: 'Revenue Earned', value: `$${totalRevenue?.toFixed(2)}`, icon: 'DollarSign', color: '#F59E0B' },
  ];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}>
      {/* Header */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--color-muted)] transition-all"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
            <Icon name="BarChart3" size={15} color="#F59E0B" />
          </div>
          <span className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Creator Analytics</span>
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} color="var(--color-muted-foreground)" />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 flex flex-col gap-4 animate-fade-in-up">
          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {ANALYTICS_STATS?.map((stat) => (
              <div
                key={stat?.label}
                className="flex flex-col items-center gap-1 p-3 rounded-xl text-center"
                style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${stat?.color}18` }}>
                  <Icon name={stat?.icon} size={14} color={stat?.color} />
                </div>
                <p className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{stat?.value}</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{stat?.label}</p>
              </div>
            ))}
          </div>

          {/* Revenue chart */}
          <div>
            <p className="text-xs font-bold mb-3" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Revenue & Downloads (6 months)</p>
            <div className="flex items-end gap-2 h-24">
              {MOCK_REVENUE?.map((d) => (
                <div key={d?.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex gap-0.5 items-end" style={{ height: 72 }}>
                    <div
                      className="flex-1 rounded-t-sm transition-all"
                      style={{ height: `${(d?.revenue / maxRevenue) * 100}%`, background: '#F59E0B', minHeight: 4 }}
                      title={`Revenue: $${d?.revenue}`}
                    />
                    <div
                      className="flex-1 rounded-t-sm transition-all"
                      style={{ height: `${(d?.downloads / maxDownloads) * 100}%`, background: 'rgba(99,102,241,0.5)', minHeight: 4 }}
                      title={`Downloads: ${d?.downloads}`}
                    />
                  </div>
                  <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{d?.month}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ background: '#F59E0B' }} />
                <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Revenue ($)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ background: 'rgba(99,102,241,0.5)' }} />
                <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Downloads</span>
              </div>
            </div>
          </div>

          {/* Top performing creations */}
          <div>
            <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Top Performing Creations</p>
            <div className="flex flex-col gap-1.5">
              {[...games]?.sort((a, b) => (b?.views || 0) - (a?.views || 0))?.slice(0, 3)?.map((game, idx) => (
                <div key={game?.id} className="flex items-center gap-3 p-2 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                  <span className="text-xs font-bold w-5 text-center" style={{ color: idx === 0 ? '#F59E0B' : idx === 1 ? '#9CA3AF' : '#CD7C2F', fontFamily: 'var(--font-data)' }}>
                    #{idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{game?.title}</p>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{(game?.views || 0)?.toLocaleString()} views</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold" style={{ color: '#F59E0B', fontFamily: 'var(--font-data)' }}>${Math.floor((game?.views || 0) * 0.008)?.toFixed(2)}</p>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>earned</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monetization CTA */}
          <div
            className="flex items-center justify-between p-3 rounded-xl"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}
          >
            <div className="flex items-center gap-2">
              <Icon name="DollarSign" size={15} color="#F59E0B" />
              <div>
                <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Enable Monetization</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Earn from your creations</p>
              </div>
            </div>
            <button
              className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-all hover:opacity-80"
              style={{ background: '#F59E0B', color: 'white', fontFamily: 'var(--font-caption)' }}
            >
              Set Up
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
